import { Anchor, Button, Checkbox, Group, PasswordInput, Stack, Text, TextInput, Paper, Title } from '@mantine/core';
import { LoginAuthenticationResponse, normalizeOperationOutcome } from '@medplum/core';
import { OperationOutcome } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { ReactNode, useEffect, useState, type JSX } from 'react';
import { IconArrowRight } from '@tabler/icons-react';
import { gtag_report_conversion } from '../GoogleTagManager';

// Helper function to initialize reCAPTCHA
const initRecaptcha = (siteKey: string) => {
  // Check if the script is already loaded
  if (document.querySelector(`script[src*="recaptcha/api.js?render=${siteKey}"]`)) {
    return; // Script already exists
  }
  
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
  document.head.appendChild(script);
};

// Helper function to get reCAPTCHA token
const getRecaptchaToken = async (siteKey: string): Promise<string> => {
  // Wait for grecaptcha to be available
  const checkRecaptchaLoaded = (): Promise<void> => {
    return new Promise((resolve) => {
      if ((window as any).grecaptcha && (window as any).grecaptcha.ready) {
        resolve();
      } else {
        setTimeout(() => checkRecaptchaLoaded().then(resolve), 100);
      }
    });
  };

  await checkRecaptchaLoaded();
  
  return new Promise((resolve) => {
    (window as any).grecaptcha.ready(() => {
      (window as any).grecaptcha
        .execute(siteKey, { action: 'submit' })
        .then((token: string) => resolve(token));
    });
  });
};

export interface CustomNewUserFormProps {
  projectId: string;
  clientId?: string;
  recaptchaSiteKey?: string;
  children?: ReactNode;
  handleAuthResponse: (response: LoginAuthenticationResponse, email?: string) => void;
  onLoading?: (loading: boolean) => void;
  onSubmitComplete?: () => void;
  isMobileApp?: boolean;
  redirectUri?: string;
}

export function CustomNewUserForm(props: CustomNewUserFormProps): JSX.Element {
  const medplum = useMedplum();
  const [outcome, setOutcome] = useState<OperationOutcome>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mobile app styling for glassmorphism background
  const mobileInputStyles = props.isMobileApp ? {
    input: {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      color: '#1f2937',
      '&:focus': {
        borderColor: 'rgba(255, 255, 255, 0.6)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
      },
      '&::placeholder': {
        color: '#6b7280',
      }
    },
    label: {
      color: 'white',
      fontWeight: 600,
    }
  } : {};

  // Initialize reCAPTCHA when component mounts - only if site key is available
  useEffect(() => {
    if (props.recaptchaSiteKey) {
      console.log('Initializing reCAPTCHA for registration form...');
      initRecaptcha(props.recaptchaSiteKey);
      
      // Clean up the script when component unmounts
      return () => {
        const scripts = document.querySelectorAll(`script[src*="recaptcha/api.js"]`);
        scripts.forEach(script => {
          if (script.parentNode) {
            console.log('Removing reCAPTCHA script on unmount');
            script.parentNode.removeChild(script);
          }
        });
        
        // Also clean up any grecaptcha related elements
        const grecaptchaElements = document.querySelectorAll('.grecaptcha-badge');
        grecaptchaElements.forEach(element => {
          if (element.parentNode) {
            element.parentNode.removeChild(element);
          }
        });
      };
    }
  }, [props.recaptchaSiteKey]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    
    try {
      let recaptchaToken = '';
      if (props.recaptchaSiteKey) {
        console.log('Getting reCAPTCHA token for form submission...');
        recaptchaToken = await getRecaptchaToken(props.recaptchaSiteKey);
      }

      const response = await medplum.startNewUser({
        projectId: props.projectId,
        clientId: props.clientId,
        firstName: formData.get('firstName') as string,
        lastName: formData.get('lastName') as string,
        email: formData.get('email') as string,
        password: formData.get('password') as string,
        remember: formData.get('remember') === 'true',
        recaptchaSiteKey: props.recaptchaSiteKey,
        recaptchaToken,
      });
      
      // Track the form submission as a conversion using the Google Ads function
      gtag_report_conversion();
      const userEmail = formData.get('email') as string;
      props.handleAuthResponse(response, userEmail);
    } catch (err) {
      setOutcome(normalizeOperationOutcome(err));
      props.onSubmitComplete?.();
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <>
      {outcome && (
        <Paper p="md" mb="md" bg="red.0" radius="md">
          <Text c="red">{outcome.issue?.[0]?.details?.text || 'An error occurred'}</Text>
        </Paper>
      )}
      
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {props.isMobileApp ? (
            <Group gap="md" grow>
              <TextInput
                name="firstName"
                label="First Name"
                placeholder="First name"
                required
                styles={mobileInputStyles}
              />
              
              <TextInput
                name="lastName"
                label="Last Name"
                placeholder="Last name"
                required
                styles={mobileInputStyles}
              />
            </Group>
          ) : (
            <>
              <TextInput
                name="firstName"
                label="First Name"
                placeholder="Enter your first name"
                required
                styles={mobileInputStyles}
              />
              
              <TextInput
                name="lastName"
                label="Last Name"
                placeholder="Enter your last name"
                required
                styles={mobileInputStyles}
              />
            </>
          )}
          
          <TextInput
            name="email"
            type="email"
            label="Email"
            placeholder="your.email@example.com"
            required
            styles={mobileInputStyles}
          />
          
          <PasswordInput
            name="password"
            label="Password"
            placeholder="Create a secure password"
            description="Password must be at least 8 characters"
            required
            styles={{
              ...mobileInputStyles,
              description: props.isMobileApp ? { color: 'rgba(255, 255, 255, 0.8)' } : {}
            }}
          />
          
          {!props.isMobileApp && (
            <Text 
              size="sm" 
              c="dimmed"
            >
              By signing up, you agree to our{' '}
              <Anchor 
                href="/privacy"
                c="teal.6"
              >
                Privacy Policy
              </Anchor>
              {' and '}
              <Anchor 
                href="/terms"
                c="teal.6"
              >
                Terms of Service
              </Anchor>
            </Text>
          )}

          <Group justify="flex-end">
            <Button 
              type="submit" 
              loading={isSubmitting}
              size="lg" 
              radius="md" 
              fullWidth
              rightSection={!isSubmitting && <IconArrowRight size={20} />}
              color={props.isMobileApp ? "blue" : "teal"}
              variant="filled"
              styles={{
                root: props.isMobileApp ? {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#3b82f6',
                  fontWeight: 700,
                  fontSize: '18px',
                  height: '56px',
                  borderRadius: '9999px',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    transform: 'translateY(-1px)',
                  }
                } : {
                  backgroundColor: '#14b8a6',
                  borderColor: '#14b8a6',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#0f9988',
                  }
                }
              }}
            >
              {isSubmitting ? 'Creating your account...' : 'Sign Up'}
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  );
}